import { Camera, FileImage, FileText, Images, Loader2, X } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import Webcam from "react-webcam";
import { toast } from "@patient-watch/ui/sonner";
import { Button } from "@patient-watch/ui/button";
import { Card } from "@patient-watch/ui/card";
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from "@patient-watch/ui/form";
import { Label } from "@patient-watch/ui/label";
import { cn } from "@patient-watch/ui/utils";
import { RichLabel } from "@patient-watch/forms";
import { useInputUploadSignedUrl } from "@/features/entry-values/hooks/useInputUploadSignedUrl";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { getToastCopy } from "@/features/i18n/toastCopy";
import { dataUrlToFile, type DocumentUploadValue, uploadDocument } from "../api/uploadDocument";
import { FileUploadPreview } from "./FileUploadPreview";
import { isImageFileName, isPdfFileName } from "../utils/fileUploadHelpers";

const ACCEPTED_MIME_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

function formatFileSize(bytes: number) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Number.parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}

function isAcceptedFile(file: File) {
    const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
    return ACCEPTED_MIME_TYPES.includes(file.type) || ["pdf", "jpg", "jpeg", "png"].includes(extension);
}

function getFileIcon(fileName: string) {
    if (isPdfFileName(fileName)) {
        return <FileText className="w-5 h-5 text-foreground" aria-hidden="true" />;
    }
    return <FileImage className="w-5 h-5 text-foreground" aria-hidden="true" />;
}

interface DocumentCaptureOverlayProps {
    onCapture: (file: File) => void;
    onClose: () => void;
}

function DocumentCaptureOverlay({ onCapture, onClose }: DocumentCaptureOverlayProps) {
    const { locale } = useAppLocale();
    const copy =
        locale === "fr-FR"
            ? { takePhoto: "Prendre une photo", cancel: "Annuler", capturePhoto: "Prendre la photo" }
            : locale === "it-IT"
              ? { takePhoto: "Scatta una foto", cancel: "Annulla", capturePhoto: "Scatta la foto" }
              : locale === "es-ES"
                ? { takePhoto: "Hacer una foto", cancel: "Cancelar", capturePhoto: "Hacer la foto" }
                : locale === "de-DE"
                  ? { takePhoto: "Foto aufnehmen", cancel: "Abbrechen", capturePhoto: "Foto aufnehmen" }
                  : locale === "da-DK"
                    ? { takePhoto: "Tag billede", cancel: "Annuller", capturePhoto: "Tag billede" }
                    : { takePhoto: "Take a photo", cancel: "Cancel", capturePhoto: "Capture photo" };
    const webcamRef = useRef<Webcam | null>(null);

    const handleCapture = useCallback(() => {
        const imageSrc = webcamRef.current?.getScreenshot();
        if (!imageSrc) {
            return;
        }
        const file = dataUrlToFile(imageSrc, `upload-${Date.now()}.jpg`);
        onCapture(file);
        onClose();
    }, [onCapture, onClose]);

    return (
        <section className="top-0 left-0 z-50 fixed flex flex-col justify-between items-center bg-black w-[100vw] h-[100vh]">
            <div className="flex justify-between items-center px-4 py-3 w-full text-white">
                <p className="font-medium text-sm">{copy.takePhoto}</p>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-white hover:text-white"
                    onClick={onClose}
                >
                    {copy.cancel}
                </Button>
            </div>
            <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                videoConstraints={{ facingMode: "environment" }}
                className="w-full max-h-[70vh] object-cover"
            />
            <div className="px-4 py-6 w-full">
                <Button type="button" className="w-full h-12" onClick={handleCapture}>
                    {copy.capturePhoto}
                </Button>
            </div>
        </section>
    );
}

interface UploadedFilePreviewProps {
    value: DocumentUploadValue;
    diaryId?: string;
    entryId?: string;
    inputId: string;
    localPreviewUrl?: string | null;
}

function UploadedFilePreview({ value, diaryId, entryId, inputId, localPreviewUrl }: UploadedFilePreviewProps) {
    const {
        data: signedUrl,
        isLoading,
        isError,
    } = useInputUploadSignedUrl({
        diaryId,
        entryId: entryId ?? "",
        inputId,
        fileName: value.fileName,
        objectId: value.objectId,
        enabled: Boolean(entryId && value.objectId && value.fileName),
    });

    const previewUrl = localPreviewUrl ?? signedUrl ?? null;

    return (
        <FileUploadPreview
            fileName={value.fileName}
            previewUrl={previewUrl}
            isLoading={!localPreviewUrl && isLoading}
            isError={!localPreviewUrl && isError}
            size="md"
        />
    );
}

interface DocumentUploadInputProps {
    name: string;
    label: string;
    description?: string;
    required?: boolean;
    diaryId?: string;
    entryId?: string;
    inputId: string;
}

const DocumentUploadInput = ({
    name,
    label,
    description,
    required = false,
    diaryId,
    entryId,
    inputId,
}: DocumentUploadInputProps) => {
    const { locale } = useAppLocale();
    const copy =
        locale === "fr-FR"
            ? {
                  acceptHint: "Types de fichiers acceptés : PDF, JPG ou PNG.",
                  maxSize: "Taille max.",
                  removeFile: "Supprimer le fichier",
                  uploading: "Envoi en cours...",
                  uploaded: "Envoyé",
                  useCamera: "Utiliser l'appareil photo",
                  choosePhotoLibrary: "Choisir dans la photothèque",
                  chooseFile: "Choisir un fichier (PDF ou image)",
              }
            : locale === "it-IT"
              ? {
                    acceptHint: "Tipi di file accettati: PDF, JPG o PNG.",
                    maxSize: "Dimensione max.",
                    removeFile: "Rimuovi il file",
                    uploading: "Caricamento...",
                    uploaded: "Caricato",
                    useCamera: "Usa la fotocamera",
                    choosePhotoLibrary: "Scegli dalla libreria foto",
                    chooseFile: "Scegli un file (PDF o immagine)",
                }
              : locale === "es-ES"
                ? {
                      acceptHint: "Tipos de archivo aceptados: PDF, JPG o PNG.",
                      maxSize: "Tamaño máx.",
                      removeFile: "Quitar el archivo",
                      uploading: "Cargando...",
                      uploaded: "Cargado",
                      useCamera: "Usar la cámara",
                      choosePhotoLibrary: "Elegir de la fototeca",
                      chooseFile: "Elegir un archivo (PDF o imagen)",
                  }
                : locale === "de-DE"
                  ? {
                        acceptHint: "Akzeptierte Dateitypen: PDF, JPG oder PNG.",
                        maxSize: "Max. Größe",
                        removeFile: "Datei entfernen",
                        uploading: "Wird hochgeladen...",
                        uploaded: "Hochgeladen",
                        useCamera: "Kamera verwenden",
                        choosePhotoLibrary: "Aus Fotobibliothek wählen",
                        chooseFile: "Datei wählen (PDF oder Bild)",
                    }
                  : locale === "da-DK"
                    ? {
                          acceptHint: "Accepterede filtyper: PDF, JPG eller PNG.",
                          maxSize: "Maks. størrelse",
                          removeFile: "Fjern fil",
                          uploading: "Uploader...",
                          uploaded: "Uploadet",
                          useCamera: "Brug kamera",
                          choosePhotoLibrary: "Vælg fra fotobibliotek",
                          chooseFile: "Vælg en fil (PDF eller billede)",
                      }
                    : {
                          acceptHint: "Accepted file types: PDF, JPG or PNG.",
                          maxSize: "Max. size",
                          removeFile: "Remove file",
                          uploading: "Uploading...",
                          uploaded: "Uploaded",
                          useCamera: "Use camera",
                          choosePhotoLibrary: "Choose from photo library",
                          chooseFile: "Choose a file (PDF or image)",
                      };

    const { control } = useFormContext();
    const generatedId = useId();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const photoLibraryInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);

    const canUpload = Boolean(diaryId && entryId && inputId);

    useEffect(() => {
        return () => {
            if (localPreviewUrl) {
                URL.revokeObjectURL(localPreviewUrl);
            }
        };
    }, [localPreviewUrl]);

    const clearInputs = () => {
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (photoLibraryInputRef.current) photoLibraryInputRef.current.value = "";
        if (cameraInputRef.current) cameraInputRef.current.value = "";
    };

    const processFile = async (file: File, onChange: (value: DocumentUploadValue | null) => void) => {
        if (!isAcceptedFile(file)) {
            toast.error(copy.acceptHint);
            return;
        }
        if (file.size > MAX_SIZE_BYTES) {
            toast.error(`File must be smaller than ${formatFileSize(MAX_SIZE_BYTES)}.`);
            return;
        }
        if (!canUpload) {
            toast.error(getToastCopy().missingEntry);
            return;
        }

        const nextPreviewUrl =
            isImageFileName(file.name) || file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
        if (localPreviewUrl) {
            URL.revokeObjectURL(localPreviewUrl);
        }
        setLocalPreviewUrl(nextPreviewUrl);

        setIsUploading(true);
        try {
            const uploaded = await uploadDocument({
                file,
                diaryId: diaryId as string,
                entryId: entryId as string,
                inputId,
            });
            onChange(uploaded);
        } catch (error) {
            console.error("File upload failed:", error);
            toast.error(error instanceof Error ? error.message : getToastCopy().unknownError);
            if (nextPreviewUrl) {
                URL.revokeObjectURL(nextPreviewUrl);
                setLocalPreviewUrl(null);
            }
        } finally {
            setIsUploading(false);
            clearInputs();
        }
    };

    const handleRemove = (onChange: (value: DocumentUploadValue | null) => void) => {
        if (localPreviewUrl) {
            URL.revokeObjectURL(localPreviewUrl);
            setLocalPreviewUrl(null);
        }
        onChange(null);
        clearInputs();
    };

    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => {
                const value = (field.value as DocumentUploadValue | null | undefined) ?? null;

                return (
                    <FormItem>
                        <Label>
                            <RichLabel>{label}</RichLabel>
                            {required ? <span className="ml-0.5 text-red-500">*</span> : null}
                        </Label>
                        {description ? <FormDescription>{description}</FormDescription> : null}

                        {cameraOpen ? (
                            <DocumentCaptureOverlay
                                onClose={() => setCameraOpen(false)}
                                onCapture={(file) => void processFile(file, field.onChange)}
                            />
                        ) : null}

                        <FormControl>
                            <div className="flex flex-col gap-3 w-full">
                                {value ? (
                                    <Card className="relative gap-4 bg-muted shadow-none p-4">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="top-1 right-1 absolute text-muted-foreground hover:text-foreground"
                                            aria-label={copy.removeFile}
                                            disabled={isUploading}
                                            onClick={() => handleRemove(field.onChange)}
                                        >
                                            <X className="w-5 h-5 shrink-0" aria-hidden="true" />
                                        </Button>
                                        <div className="flex items-center space-x-2.5 pr-8">
                                            <span className="flex flex-shrink-0 justify-center items-center bg-background shadow-sm ring-border rounded-sm ring-1 ring-inset w-10 h-10">
                                                {getFileIcon(value.fileName)}
                                            </span>
                                            <div className="min-w-0">
                                                <p className="font-medium text-foreground text-xs truncate">
                                                    {value.fileName}
                                                </p>
                                                <p className="mt-0.5 text-muted-foreground text-xs">
                                                    {isUploading ? copy.uploading : copy.uploaded}
                                                </p>
                                            </div>
                                        </div>
                                        <UploadedFilePreview
                                            value={value}
                                            diaryId={diaryId}
                                            entryId={entryId}
                                            inputId={inputId}
                                            localPreviewUrl={localPreviewUrl}
                                        />
                                    </Card>
                                ) : (
                                    <>
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            className="w-full h-12"
                                            disabled={isUploading || !canUpload}
                                            onClick={() => {
                                                if (
                                                    typeof navigator !== "undefined" &&
                                                    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
                                                ) {
                                                    cameraInputRef.current?.click();
                                                    return;
                                                }
                                                setCameraOpen(true);
                                            }}
                                        >
                                            {isUploading ? (
                                                <Loader2 className="w-5 h-5 animate-spin" />
                                            ) : (
                                                <Camera className="w-5 h-5" />
                                            )}
                                            <span className="ml-2">{copy.useCamera}</span>
                                        </Button>

                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-full h-12"
                                            disabled={isUploading || !canUpload}
                                            onClick={() => photoLibraryInputRef.current?.click()}
                                        >
                                            <Images className="w-5 h-5" />
                                            <span className="ml-2">{copy.choosePhotoLibrary}</span>
                                        </Button>

                                        <button
                                            type="button"
                                            disabled={isUploading || !canUpload}
                                            className={cn(
                                                "flex justify-center px-6 py-8 border border-input border-dashed rounded-md w-full",
                                                isUploading || !canUpload
                                                    ? "opacity-60 cursor-not-allowed"
                                                    : "cursor-pointer"
                                            )}
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <div className="text-center">
                                                {isUploading ? (
                                                    <Loader2 className="mx-auto w-10 h-10 text-muted-foreground animate-spin" />
                                                ) : (
                                                    <FileImage
                                                        className="mx-auto w-10 h-10 text-muted-foreground"
                                                        aria-hidden="true"
                                                    />
                                                )}
                                                <p className="mt-2 text-muted-foreground text-sm leading-6">
                                                    {copy.chooseFile}
                                                </p>
                                            </div>
                                        </button>
                                    </>
                                )}

                                <input
                                    id={generatedId}
                                    ref={fileInputRef}
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                                    disabled={isUploading || !canUpload}
                                    tabIndex={-1}
                                    aria-hidden="true"
                                    onChange={(event) => {
                                        const file = event.target.files?.[0];
                                        if (file) {
                                            void processFile(file, field.onChange);
                                        }
                                    }}
                                />
                                <input
                                    ref={photoLibraryInputRef}
                                    type="file"
                                    className="hidden"
                                    accept="image/jpeg,image/png,image/*"
                                    disabled={isUploading || !canUpload}
                                    tabIndex={-1}
                                    aria-hidden="true"
                                    onChange={(event) => {
                                        const file = event.target.files?.[0];
                                        if (file) {
                                            void processFile(file, field.onChange);
                                        }
                                    }}
                                />
                                <input
                                    ref={cameraInputRef}
                                    type="file"
                                    className="hidden"
                                    accept="image/*"
                                    capture="environment"
                                    disabled={isUploading || !canUpload}
                                    tabIndex={-1}
                                    aria-hidden="true"
                                    onChange={(event) => {
                                        const file = event.target.files?.[0];
                                        if (file) {
                                            void processFile(file, field.onChange);
                                        }
                                    }}
                                />

                                {!value ? (
                                    <p className="text-muted-foreground text-xs leading-5">
                                        <span>{copy.acceptHint}</span>
                                        <span className="block sm:inline sm:ml-2">
                                            {copy.maxSize}: {formatFileSize(MAX_SIZE_BYTES)}
                                        </span>
                                    </p>
                                ) : null}
                            </div>
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
};

export default DocumentUploadInput;
